import { useEffect, useState } from 'react'
import BackofficeToastStack from './BackofficeToastStack'
import OverrideSheet from './OverrideSheet'
import EventFeedPanel from './panels/EventFeedPanel'
import LiveMatchesPanel from './panels/LiveMatchesPanel'
import OverridesPanel from './panels/OverridesPanel'
import PeoplePanel from './panels/PeoplePanel'
import PipelineHealthPanel from './panels/PipelineHealthPanel'
import SeasonControlPanel from './panels/SeasonControlPanel'
import AuditPanel from './panels/AuditPanel'
import { useAdminSummary } from '../../app/backoffice/useAdminSummary'
import { applyMute, useEventFeed } from '../../app/backoffice/useEventFeed'
import Toast from '../Toast'
import type { MatchView } from '../../adminApi'
import './Backoffice.css'

/**
 * The backoffice shell.
 *
 * Renders a list of panels and **owns no panel's state**: every panel fetches its own data and
 * renders its own error, so a failing health endpoint cannot blank the live-matches panel next to
 * it. What lives here is the header, the summary alerts, the cross-panel toast, and the one piece
 * of genuinely shared state — which fixture the override sheet is open on, since it is opened from
 * one panel and changes what another shows.
 *
 * This module is loaded by a dynamic `import()` behind an admin check, so a non-admin's browser
 * never downloads it. That is defence in depth, not the control — the control is the server's 403.
 */
export default function BackofficeScreen({ onBack }: { onBack: () => void }) {
  const summary = useAdminSummary(true)
  const feed = useEventFeed({ enabled: true })
  const [correcting, setCorrecting] = useState<MatchView | null>(null)
  const [toast, setToast] = useState<string | null>(null)
  const [nowMs, setNowMs] = useState(() => Date.now())

  // One clock for every relative timestamp on the screen, so "2m ago" everywhere means the same
  // instant and the page does not re-render once per panel per second.
  useEffect(() => {
    const id = setInterval(() => setNowMs(Date.now()), 5_000)
    return () => clearInterval(id)
  }, [])

  // Escape backs out, matching every other pushed screen in the app.
  useEffect(() => {
    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape' && !correcting) onBack()
    }
    document.addEventListener('keydown', onKeyDown)
    return () => document.removeEventListener('keydown', onKeyDown)
  }, [correcting, onBack])

  const mode = summary.data?.mode ?? 'mock'

  return (
    <div className="backoffice">
      <header className="backoffice__header">
        <button type="button" className="backoffice__back" onClick={onBack}>
          ← Back
        </button>
        <p className="backoffice__title">Backoffice</p>
        {/* What you may do, and to what, depends entirely on this — so it is never out of sight. */}
        <span className={`backoffice__mode backoffice__mode--${mode}`}>{mode}</span>
      </header>

      {summary.data && summary.data.alerts.length > 0 && (
        <div className="backoffice__alerts">
          {summary.data.alerts.map((alert, index) => (
            <p key={`${alert.kind}-${alert.target ?? index}`} className={`bo-alert bo-alert--${alert.severity}`}>
              {alert.message}
            </p>
          ))}
        </div>
      )}
      {summary.error !== null && (
        <div className="backoffice__alerts">
          <p className="bo-alert bo-alert--error">summary unavailable: {summary.error}</p>
        </div>
      )}

      <div className="backoffice__panels">
        <LiveMatchesPanel enabled onCorrect={setCorrecting} />
        <EventFeedPanel feed={feed} nowMs={nowMs} />
        <PipelineHealthPanel enabled />
        <OverridesPanel enabled onChanged={setToast} />
        <SeasonControlPanel enabled onToast={setToast} />
        <PeoplePanel enabled mode={mode} onToast={setToast} />
        <AuditPanel enabled />
      </div>

      {/* Only ever inside this screen: no admin gets goal toasts over the predictions grid. */}
      <BackofficeToastStack events={applyMute(feed.events, feed.mute)} />

      {correcting && (
        <OverrideSheet
          match={correcting}
          onClose={() => setCorrecting(null)}
          onSaved={(result) => {
            setCorrecting(null)
            // Re-scoring is never automatic — it moves every leaderboard, so it is offered as an
            // explicit next step and the operator presses it themselves.
            setToast(
              result.requiresRescore
                ? `Override saved. GW${result.gameweek} is already scored — ${result.affectedUsers} user(s) will change. Re-score it from Season & gameweek.`
                : 'Override saved.'
            )
          }}
        />
      )}

      {/* The app's own single-message toast is right for these: they are confirmations of
          something *you* just did, not a feed. */}
      {toast && <Toast message={toast} onDismiss={() => setToast(null)} />}
    </div>
  )
}
